// Copyright Epic Games, Inc. All Rights Reserved.

import { json } from "@remix-run/cloudflare";
import { useLoaderData } from "@remix-run/react";

import { getSdk } from "~/generated/schema.graphcms";
import { mergeMeta } from "~/metatags/mergeMeta";
import { getGraphCms, getStage } from "~/services/graphcms";
import { isValidArticle } from "~/typings";
import { getDictionary } from "~/utils/dictionaries";
import { localeTokenMap } from "~/utils/localeTokenMap";

import PlayNowModal from "./_index/components/home/PlayNowModal";
import ArticleContent from "./_index/components/news/ArticleContent";

import type { LoaderFunctionArgs } from "@remix-run/cloudflare";

import type { Locale } from "~/generated/schema.graphcms";
import type { Locale as AppLocale } from "~/i18n.config";
import type { NewsArticle, NewsArticleContent } from "~/typings";
import type { Dictionary } from "~/utils/dictionaries";

export const meta = mergeMeta<typeof loader>(({ data }) => {
	if (!data) {
		return [{ title: "Not found" }, { property: "description", content: "No article has been found" }];
	}

	const { articleData } = data;
	if (!articleData) {
		return [{ title: "Not found" }, { property: "description", content: "No article has been found" }];
	}

	return [
		{ title: articleData.title },
		{ property: "description", content: articleData.description },
		{ property: "authors", content: articleData.author },
		{ property: "og:title", content: articleData.title },
		{ property: "og:description", content: articleData.description },
		{ property: "og:image", content: articleData.image?.url },
		{ property: "twitter:title", content: articleData.title },
		{ property: "twitter:description", content: articleData.description },
	];
});

async function getArticleData(env: Env, mappedLocaleToken: string, slug: string): Promise<NewsArticle> {
	const { GetArticle } = getSdk(getGraphCms(env));
	const articleData = await GetArticle({
		locale: [mappedLocaleToken, "en"] as Locale[],
		slug: slug,
		stage: getStage(env),
	});

	if (!isValidArticle(articleData?.article)) {
		throw new Response(undefined, { status: 404 });
	}

	return articleData.article;
}

export default function Article(): React.ReactNode {
	const { articleData, dict, locale } = useLoaderData<typeof loader>();

	return (
		<>
			<div className="article-wrapper">
				<article className="container">
					<div className="columns is-centered">
						<div className="column is-three-quarters">
							<h1 className="article-title title is-size-1 my-6">{articleData.title}</h1>
							<h2 className="subtitle has-text-centered">
								{articleData.date} | {articleData.author?.name}
							</h2>
						</div>
					</div>
					<div className="columns is-centered">
						<div className="column is-full">
							<img
								src={articleData.image?.url ?? ""}
								width={1920}
								height={1080}
								alt={articleData.image?.fileName ?? ""}
							/>
						</div>
					</div>
					<div className="article-content columns is-centered px-4 pb-6">
						<div className="column is-three-quarters">
							<ArticleContent
								legacyContent={articleData.legacyContent}
								content={articleData.content as NewsArticleContent}
							/>
						</div>
					</div>
				</article>
			</div>
			<PlayNowModal locale={locale} copy={dict} />
		</>
	);
}

export const loader = async ({ params, context }: LoaderFunctionArgs) => {
	const { locale } = context;
	const { slug } = params;

	if (slug === undefined) {
		throw new Response("Page Not Found", {
			status: 404,
		});
	}

	const { env } = context.cloudflare;

	const mappedLocaleToken = localeTokenMap(locale);
	const dict = await getDictionary(locale);
	const articleData = await getArticleData(env, mappedLocaleToken, slug);

	return json<{
		dict: Dictionary;
		articleData: NewsArticle;
		locale: AppLocale;
	}>({
		dict,
		articleData,
		locale,
	});
};
